import { ISearchResult, IResults, IErrorType } from '../state-helpers/search-results';
import { ICategoryResult } from '../state-helpers/categories';
import { urlReplaceInAction, urlPushAction, UrlQueryParamTypes, encode } from 'react-url-query';
import { fetchResults } from './results';
import { GlobalActionType } from '../../global/actions/index';
import * as Rx from 'rx';
import * as _ from 'lodash';

export const CHANGE_QUERY = 'CHANGE_QUERY';

export const changeSearchTerm = urlReplaceInAction(CHANGE_QUERY, 'q', UrlQueryParamTypes.string);

// Enums
export class ActionType extends GlobalActionType {
    static SET_PAGE: string = "SET_PAGE";
    static SET_PAGE_TITLE: string = "SET_PAGE_TITLE";
    static SET_SELECTED_CATEGORY_COUNT: string = "SET_SELECTED_CATEGORY_COUNT";
    static RESULTS_LOADED: string = "RESULTS_LOADED";
    static RESULTS_LOADING: string = "RESULTS_LOADING";
    static RESULTS_LOAD_FAILURE: string = "RESULTS_LOAD_FAILURE";
    static SHOW_RESULTS: string = "SHOW_RESULTS";
    static SET_SEARCH_TERM: string = "SET_SEARCH_TERM";
    static SET_MOBILE_CATEGORIES_VISIBILITY: string = "SET_MOBILE_CATEGORIES_VISIBILITY";
    static UPDATE_CATEGORIES: string = "UPDATE_CATEGORIES";
    static TOGGLE_MOBILE_NAV: string = "TOGGLE_MOBILE_NAV";
    static BROWSER_NAVIGATION: string = "BROWSER_NAVIGATION";
    static UPDATE_QUERY: string = "UPDATE_QUERY";
    static CLEAR_ERROR_TEXT: string = "CLEAR_ERROR_TEXT";
}

export enum ActionOrigin {
    BROWSER_NAVIGATION
}


// Interfaces
export interface IAction {
    type: ActionType;
    origin?: ActionOrigin;
}

export interface IResultsLoadedAction extends IAction {
    results: ISearchResult[];
    categories: ICategoryResult[];
    page: number;
    errorMessage: string;
    errorType: IErrorType;
    trackingId: string;
    totalNumber: number;
    totalPages: number;
    showResults?: boolean;
    isLoading?: boolean;
    noResultsWYSIWYG: string;
}

export interface ISetSelectedCategoryCount extends IAction {
    selectedCategoryCount: number;
}

export interface ISearchIsLoading extends IAction {
    isLoading: boolean;
}

export interface IShowResults extends IAction {
    showResults: boolean;
}

export interface IUpdateSearchCategory extends IAction {
    categories: ICategoryResult[]
}

export interface ISetPageAction extends IAction {
    page: number;
}

export interface ISetSearchTermAction extends IAction {
    searchTerm: string;
}

export interface ISetMobileCategoriesVisibility extends IAction {
    visible: boolean;
}

// Actions
export function itemsAreLoading(isLoading: boolean): ISearchIsLoading {
    return {
        type: ActionType.RESULTS_LOADING,
        isLoading: isLoading
    }
}

export function showResults(showResults: boolean): IShowResults {
    return {
        type: ActionType.SHOW_RESULTS,
        showResults: showResults
    }
}

export function noResults(): IResultsLoadedAction {
    return {
        type: ActionType.SET_PAGE,
        categories: [],
        results: [],
        page: 1,
        errorMessage: '',
        totalNumber: 0,
        trackingId: '',
        totalPages: 0,
        errorType: IErrorType.NONE,
        noResultsWYSIWYG: ""
    }
}

export function getResults(results: IResults): IResultsLoadedAction {
    return {
        type: ActionType.RESULTS_LOADED,
        results: results.searchItemList,
        categories: results.categories,
        page: results.currentPage,
        errorMessage: '',
        totalNumber: results.totalNumber,
        totalPages: results.totalPages,
        trackingId: results.trackingId,
        errorType: IErrorType.NONE,
        noResultsWYSIWYG: results.noResultsWYSIWYG
    }
}

export function clearErrorText(results: IResults): IResultsLoadedAction {
    return {
        type: ActionType.CLEAR_ERROR_TEXT,
        results: results.searchItemList,
        categories: results.categories,
        page: results.currentPage,
        errorMessage: '',
        totalNumber: results.totalNumber,
        trackingId: results.trackingId,
        totalPages: results.totalPages,
        errorType: IErrorType.NONE,
        noResultsWYSIWYG: results.noResultsWYSIWYG
    }
}


export function updateResultsFailure(errorMessage: string): IResultsLoadedAction {
    var errorType;
    switch (errorMessage) {
        case "Bad response":
            errorType = IErrorType.GENERAL;
            break;
        case "Failed to fetch":
            errorType = IErrorType.TIME_OUT;
            break;
        default:
            errorType = IErrorType.NONE;
            break;
    }

    return {
        type: ActionType.RESULTS_LOAD_FAILURE,
        errorMessage: errorMessage,
        results: [],
        categories: [],
        page: 1,
        totalNumber: 0,
        totalPages: 0,
        trackingId: '',
        errorType: errorType,
        noResultsWYSIWYG: ""
    }
}

export function setPage(page: number): ISetPageAction {
    return {
        type: ActionType.SET_PAGE,
        page
    };
}

export interface ISearchTextUpdatedAction extends IAction {
    searchText: string;
}

export interface IBrowserAction extends IAction, ISearchTextUpdatedAction, ISetSearchTermAction, ISetPageAction { }

export function setSearchTerm(searchTerm: string): ISetSearchTermAction {
    return {
        type: ActionType.SET_SEARCH_TERM,
        searchTerm
    };
}

export function setMobileCategoriesVisibility(visible: boolean): ISetMobileCategoriesVisibility {
    return {
        type: ActionType.SET_MOBILE_CATEGORIES_VISIBILITY,
        visible
    };
}

export function setSelectedCategoryCount(selectedCategoryCount: number): ISetSelectedCategoryCount {
    return {
        type: ActionType.SET_SELECTED_CATEGORY_COUNT,
        selectedCategoryCount
    };
}

export function setPageTitle(searchTerm: string, contextId: string): IAction {
    return {
        type: ActionType.SET_PAGE_TITLE
    };
}

export function browser(action: IAction) {
    return _.assign(action, { origin: ActionOrigin.BROWSER_NAVIGATION }) as IAction;
}

export function updateResults(results: IResults, page = 1): IResultsLoadedAction {
    return {
        type: ActionType.RESULTS_LOADED,
        results: results.searchItemList,
        categories: results.categories,
        page: results.currentPage,
        errorMessage: results.errorMessage,
        errorType: results.errorType,
        totalNumber: results.totalNumber,
        totalPages: results.totalPages,
        trackingId: results.trackingId,
        isLoading: results.loading,
        noResultsWYSIWYG: results.noResultsWYSIWYG
    };
}

export function updateSearchText(searchText: string) {
    return (dispatch, getState) => {
        dispatch({ type: ActionType.UPDATE_QUERY, searchText });
        return fetchResults();
    }
}

export function updateSearchCategory(categories: ICategoryResult[]): IUpdateSearchCategory {
    return {
        type: ActionType.UPDATE_CATEGORIES,
        categories
    }
}